import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  type Company,
  type Counterparty,
  type EntrySide,
  type FiscalYear,
  type JournalEntry,
  type NewJournalLine,
  type OpenItemsReport,
  type OpeningBalancePreview,
  type PostingProposal,
  type TrialBalance,
} from '../api';
import { FxRevaluationPanel } from '../components/FxRevaluation';
import { SettlementsView } from '../components/SettlementsView';
import type { Dictionary, Locale } from '../i18n';
import { amount as money, date as written } from '../format';
import * as Icon from '../components/Icons';
import { ErrorAlert, failureOf, type Failure } from '../components/Alerts';
import { FiscalYearGap } from '../components/FiscalYearGap';
import type { Panel } from '../components/Shell';
import { fiscalYearLabel } from '../fiscalYear';

/**
 * Dziennik i zestawienie obrotów i sald (E7-S3).
 *
 * Dwa widoki tych samych zapisów: dziennik czyta się chronologicznie („co się wydarzyło i kiedy"),
 * zestawienie — po kontach („co na nich siedzi"). Stoją na jednym ekranie, bo księgowa przechodzi
 * między nimi bez przerwy: znajduje różnicę w zestawieniu i szuka zapisu, który ją zrobił.
 */

const EMPTY_LINE: NewJournalLine = { account: '', side: 'DEBIT', amount: '' };

/**
 * Różnica między stronami dekretu.
 *
 * Liczona przy każdym uderzeniu w klawisz, bo to ona jest całą potrzebną informacją: zapis,
 * który się nie bilansuje, i tak zostanie odrzucony, a człowiek ma wiedzieć **o ile**, zanim
 * kliknie zapisz.
 */
export function difference(lines: NewJournalLine[]): number {
  return lines.reduce((sum, line) => {
    const value = Number((line.amount || '0').replace(',', '.'));
    if (!Number.isFinite(value)) return sum;
    return line.side === 'DEBIT' ? sum + value : sum - value;
  }, 0);
}

function currentYear(years: FiscalYear[]): FiscalYear | null {
  const today = new Date().toISOString().slice(0, 10);
  return years.find((year) => year.startsOn <= today && year.endsOn >= today) ?? years[0] ?? null;
}

export function JournalPanel({
  company,
  t,
  locale,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  onGo: (panel: Panel) => void;
}) {
  const [years, setYears] = useState<FiscalYear[] | null>(null);
  const [yearId, setYearId] = useState<string | null>(null);
  const [view, setView] = useState<'journal' | 'balance' | 'pending' | 'settlements' | 'fx'>('pending');
  const [items, setItems] = useState<OpenItemsReport | null>(null);
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [counterparties, setCounterparties] = useState<Counterparty[] | null>(null);
  /** Podgląd bilansu otwarcia z poprzedniego roku — otwierany na żądanie, nie przy każdym wejściu. */
  const [opening, setOpening] = useState<OpeningBalancePreview | null>(null);
  const [openingDone, setOpeningDone] = useState(false);
  const [pending, setPending] = useState<PostingProposal[] | null>(null);
  /** Zaznaczone do zaksięgowania; pusty zbiór znaczy „wszystkie, które da się zaksięgować". */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [period, setPeriod] = useState('');
  const [entries, setEntries] = useState<JournalEntry[] | null>(null);
  const [balance, setBalance] = useState<TrialBalance | null>(null);
  const [form, setForm] = useState<{ postingDate: string; description: string; lines: NewJournalLine[] } | null>(null);
  const [reversing, setReversing] = useState<{ id: string; reason: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const editable = company.roles.includes('ACCOUNTANT');

  useEffect(() => {
    api
      .fiscalYears(company.id)
      .then((loaded) => {
        setYears(loaded);
        setYearId((current) => current ?? currentYear(loaded)?.id ?? null);
      })
      .catch((failure) => setError(failureOf(failure)));
  }, [company.id]);

  const load = useCallback(async () => {
    if (!yearId) return;
    setError(null);
    try {
      const [journal, trial, waiting, open] = await Promise.all([
        api.journal(company.id, yearId, period || undefined),
        api.trialBalance(company.id, yearId, period || undefined),
        api.pendingPostings(company.id, yearId),
        api.openItems(company.id, yearId, asOf, onlyOpen),
      ]);
      setEntries(journal);
      setBalance(trial);
      setPending(waiting);
      setItems(open);
    } catch (failure) {
      setEntries(null);
      setBalance(null);
      setError(failureOf(failure));
    }
  }, [company.id, yearId, period, asOf, onlyOpen]);

  // Kontrahenci do wierszy dekretu — ładowani raz, gdy formularz się otwiera
  useEffect(() => {
    if (!form || counterparties) return;
    api
      .counterparties(company.id, '', false)
      .then(setCounterparties)
      .catch(() => setCounterparties([]));
  }, [form, counterparties, company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const year = years?.find((item) => item.id === yearId) ?? null;
  const closed = year?.status === 'CLOSED';
  const gap = useMemo(() => (form ? difference(form.lines) : 0), [form]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.journal.title}</h3>
          <p>{t.journal.lead}</p>
        </div>
        <div className="sp">
          <div className="seg" role="group" aria-label={t.journal.view}>
            <button type="button" aria-pressed={view === 'pending'} onClick={() => setView('pending')}>
              {t.journal.pending}
              {pending && pending.length > 0 ? ` (${pending.length})` : ''}
            </button>
            <button type="button" aria-pressed={view === 'journal'} onClick={() => setView('journal')}>
              {t.journal.open}
            </button>
            <button type="button" aria-pressed={view === 'balance'} onClick={() => setView('balance')}>
              {t.journal.trialBalance}
            </button>
            <button type="button" aria-pressed={view === 'settlements'} onClick={() => setView('settlements')}>
              {t.journal.settlements}
            </button>
            {/* Wycena walut stoi przy księgach, bo to zapis w dzienniku na wskazany dzień (E7-S5) */}
            <button type="button" aria-pressed={view === 'fx'} onClick={() => setView('fx')}>
              {t.journal.fx.tab}
            </button>
          </div>
          <select
            className="inp w"
            aria-label={t.journal.year}
            value={yearId ?? ''}
            onChange={(event) => setYearId(event.target.value)}
          >
            {years?.map((item) => (
              <option key={item.id} value={item.id}>
                {fiscalYearLabel(item)}
              </option>
            ))}
          </select>
          <input
            className="inp w"
            type="month"
            aria-label={t.journal.month}
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
          />
        </div>
      </div>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      <FiscalYearGap
        companyId={company.id}
        roles={company.roles}
        years={years}
        t={t}
        onOpened={() => {
          // Nowy rok od razu staje się tym wybranym — po to się go otwiera
          void api.fiscalYears(company.id).then((loaded) => {
            setYears(loaded);
            setYearId(currentYear(loaded)?.id ?? null);
          });
        }}
      />

      {editable && !closed && view === 'journal' && !form && (
        <div className="row">
          <button
            type="button"
            className="btn"
            onClick={() =>
              setForm({
                postingDate: new Date().toISOString().slice(0, 10),
                description: '',
                lines: [{ ...EMPTY_LINE }, { ...EMPTY_LINE, side: 'CREDIT' }],
              })
            }
          >
            <Icon.Plus />
            {t.journal.newEntry}
          </button>
          {/* Bilans otwarcia z poprzedniego roku — gdy rok nie ma jeszcze żadnego zapisu otwarcia */}
          {!opening && !entries?.some((entry) => entry.openingBalance) && (
            <button
              type="button"
              className="btn ghost"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  setOpening(await api.openingBalancePreview(company.id, yearId!));
                })
              }
            >
              {t.journal.openingPreview}
            </button>
          )}
        </div>
      )}

      {openingDone && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{t.journal.openingDone}</b>
          </div>
        </div>
      )}

      {opening && view === 'journal' && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.journal.openingTitle}</h4>
              <p>
                {t.journal.openingLead} · {opening.previousYearStartsOn.slice(0, 4)} · {t.journal.openingResult}: {money(opening.result, locale)}
              </p>
            </div>
            <div className="sp">
              <button type="button" className="btn ghost" onClick={() => setOpening(null)}>
                {t.journal.cancel}
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy || opening.lines.length === 0}
                onClick={() =>
                  void run(async () => {
                    await api.recordOpeningBalance(company.id, yearId!, opening.existingEntryId !== null);
                    setOpening(null);
                    setOpeningDone(true);
                  })
                }
              >
                {opening.existingEntryId ? t.journal.openingReplace : t.journal.openingRecord}
              </button>
            </div>
          </div>
          {opening.existingEntryId && (
            <div className="alert a-warn">
              <Icon.Warn />
              <div>{t.journal.openingExists}</div>
            </div>
          )}
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.journal.account}</th>
                  <th className="wrap">{t.journal.name}</th>
                  <th className="wrap">{t.journal.openingCounterparty}</th>
                  <th className="wrap">{t.journal.openingDocument}</th>
                  <th className="n">{t.journal.debit}</th>
                  <th className="n">{t.journal.credit}</th>
                </tr>
              </thead>
              <tbody>
                {opening.lines.map((line, index) => (
                  <tr key={index}>
                    <td className="mono">{line.account}</td>
                    <td className="wrap">{line.accountName}</td>
                    <td className="wrap">{line.counterpartyName ?? ''}</td>
                    <td className="wrap">
                      {line.documentNumber ?? ''}
                      {line.dueDate && <span className="hint"> · {written(line.dueDate, locale)}</span>}
                    </td>
                    <td className="n mono">{line.side === 'DEBIT' ? money(line.amount, locale) : ''}</td>
                    <td className="n mono">{line.side === 'CREDIT' ? money(line.amount, locale) : ''}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>
                    <b>{t.journal.totals}</b>
                  </td>
                  <td className="n mono">
                    <b>{money(opening.totalDebit, locale)}</b>
                  </td>
                  <td className="n mono">
                    <b>{money(opening.totalCredit, locale)}</b>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {form && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.journal.newEntry}</h4>
              <p>{t.journal.formHint}</p>
            </div>
          </div>
          <div className="row">
            <label className="fld">
              <span>{t.journal.postingDate}</span>
              <input
                className="inp"
                type="date"
                value={form.postingDate}
                onChange={(event) => setForm({ ...form, postingDate: event.target.value })}
              />
            </label>
            <label className="fld" style={{ flex: 1 }}>
              <span>{t.journal.description}</span>
              <input
                className="inp"
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
            </label>
          </div>

          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.journal.account}</th>
                  <th>{t.journal.side}</th>
                  <th className="n">{t.journal.amount}</th>
                  <th>{t.journal.counterparty}</th>
                  <th>{t.journal.settlement}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {form.lines.map((line, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        className="inp mono sm"
                        style={{ maxWidth: 140 }}
                        value={line.account}
                        placeholder="200"
                        aria-label={`${t.journal.account} ${index + 1}`}
                        onChange={(event) => {
                          const lines = [...form.lines];
                          lines[index] = { ...line, account: event.target.value };
                          setForm({ ...form, lines });
                        }}
                      />
                    </td>
                    <td>
                      <select
                        className="inp sm"
                        style={{ maxWidth: 90 }}
                        value={line.side}
                        aria-label={`${t.journal.side} ${index + 1}`}
                        onChange={(event) => {
                          const lines = [...form.lines];
                          lines[index] = { ...line, side: event.target.value as EntrySide };
                          setForm({ ...form, lines });
                        }}
                      >
                        <option value="DEBIT">{t.journal.debit}</option>
                        <option value="CREDIT">{t.journal.credit}</option>
                      </select>
                    </td>
                    <td className="n">
                      <input
                        className="inp mono sm"
                        style={{ maxWidth: 120, textAlign: 'right' }}
                        value={line.amount}
                        inputMode="decimal"
                        aria-label={`${t.journal.amount} ${index + 1}`}
                        onChange={(event) => {
                          const lines = [...form.lines];
                          lines[index] = { ...line, amount: event.target.value };
                          setForm({ ...form, lines });
                        }}
                      />
                    </td>
                    <td>
                      <select
                        className="inp sm"
                        style={{ maxWidth: 200 }}
                        value={line.counterpartyId ?? ''}
                        aria-label={`${t.journal.counterparty} ${index + 1}`}
                        onChange={(event) => {
                          const lines = [...form.lines];
                          lines[index] = { ...line, counterpartyId: event.target.value || null, settlementId: null };
                          setForm({ ...form, lines });
                        }}
                      >
                        <option value="">{t.journal.counterpartyNone}</option>
                        {counterparties?.map((counterparty) => (
                          <option key={counterparty.id} value={counterparty.id}>
                            {counterparty.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {/* Otwarte pozycje tego kontrahenta na tym koncie; bez wyboru powstaje nowa */}
                      {line.counterpartyId && (
                        <select
                          className="inp sm"
                          style={{ maxWidth: 220 }}
                          value={line.settlementId ?? ''}
                          aria-label={`${t.journal.settlement} ${index + 1}`}
                          onChange={(event) => {
                            const lines = [...form.lines];
                            lines[index] = { ...line, settlementId: event.target.value || null };
                            setForm({ ...form, lines });
                          }}
                        >
                          <option value="">{t.journal.settlementNew}</option>
                          {items?.items
                            .filter((item) => item.counterpartyId === line.counterpartyId && item.account === line.account.trim() && Number(item.remaining) !== 0)
                            .map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.documentNumber ?? item.documentDate} · {money(item.remaining, locale)}
                              </option>
                            ))}
                        </select>
                      )}
                    </td>
                    <td className="act">
                      {form.lines.length > 2 && (
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() =>
                            setForm({ ...form, lines: form.lines.filter((_, at) => at !== index) })
                          }
                        >
                          {t.journal.removeLine}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row">
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => setForm({ ...form, lines: [...form.lines, { ...EMPTY_LINE }] })}
            >
              {t.journal.addLine}
            </button>
            {/* Różnica jest całą potrzebną informacją — i lepiej, żeby padła przed zapisem */}
            <span className={gap === 0 ? 'chip st-ok' : 'chip st-warn'}>
              {gap === 0 ? t.journal.balanced : `${t.journal.difference}: ${money(String(Math.abs(gap)), locale)}`}
            </span>
          </div>

          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api.recordEntry(company.id, yearId!, {
                    postingDate: form.postingDate,
                    description: form.description,
                    lines: form.lines.filter((line) => line.account && line.amount),
                  });
                  setForm(null);
                })
              }
            >
              {t.journal.save}
            </button>
            <button type="button" className="btn plain" onClick={() => setForm(null)}>
              {t.journal.cancel}
            </button>
          </div>
        </div>
      )}

      {/*
        * Lista „do zaksięgowania".
        *
        * Dokument nie księguje się sam w chwili zatwierdzenia: zapis w dzienniku jest
        * nieodwracalny, a poprawka to storno, więc księgowa dostaje moment na spojrzenie.
        * Dekret widać tu w całości — razem z kontami, stronami i kwotami — zanim cokolwiek
        * trafi do ksiąg.
        */}
      {view === 'pending' && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.journal.pending}</h4>
              <p>{t.journal.pendingHint}</p>
            </div>
            {editable && !closed && pending && pending.some((item) => item.postable) && (
              <div className="sp">
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      const wanted = pending
                        .filter((item) => item.postable && (picked.size === 0 || picked.has(item.documentId)))
                        .map((item) => item.documentId);
                      await api.postDocuments(company.id, yearId!, wanted);
                      setPicked(new Set());
                    })
                  }
                >
                  {picked.size > 0 ? t.journal.postPicked(picked.size) : t.journal.postAll}
                </button>
              </div>
            )}
          </div>

          {pending?.length === 0 && <p className="muted">{t.journal.nothingPending}</p>}

          {pending?.map((item) => (
            <div className="panel" key={item.documentId}>
              <div className="hd">
                <div>
                  <h4>
                    {editable && !closed && item.postable && (
                      <label className="check" style={{ display: 'inline-flex', marginRight: 8 }}>
                        <input
                          type="checkbox"
                          checked={picked.has(item.documentId)}
                          onChange={(event) => {
                            const next = new Set(picked);
                            if (event.target.checked) next.add(item.documentId);
                            else next.delete(item.documentId);
                            setPicked(next);
                          }}
                          aria-label={`${t.journal.postPicked(1)} — ${item.number}`}
                        />
                      </label>
                    )}
                    <span className="mono">{item.number}</span>
                  </h4>
                  <p>
                    {written(item.date, locale)}
                    {item.counterpartyName && ` · ${item.counterpartyName}`}
                    {item.schemeName && ` · ${item.schemeName}`}
                  </p>
                </div>
                <div className="sp">
                  <span className="mono">{money(item.gross, locale)}</span>
                </div>
              </div>

              {item.problem ? (
                <div className="alert a-warn">
                  <Icon.Warn />
                  <div>
                    <b>{item.problem}</b>
                  </div>
                  <button type="button" className="btn" onClick={() => onGo('chart')}>
                    {t.chart.open}
                  </button>
                </div>
              ) : (
                <div className="tbl">
                  <table>
                    <thead>
                      <tr>
                        <th>{t.journal.account}</th>
                        <th>{t.journal.name}</th>
                        <th>{t.journal.side}</th>
                        <th className="n">{t.journal.amount}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.lines.map((line, index) => (
                        <tr key={index}>
                          <td className="mono">{line.account}</td>
                          <td className="wrap">{line.accountName}</td>
                          <td>{line.side === 'DEBIT' ? t.journal.debit : t.journal.credit}</td>
                          <td className="n mono">{money(line.amount, locale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {view === 'settlements' && (
        <SettlementsView
          report={items}
          t={t}
          locale={locale}
          editable={editable && !closed}
          asOf={asOf}
          onlyOpen={onlyOpen}
          onAsOf={setAsOf}
          onOnlyOpen={setOnlyOpen}
          onOffset={(offset) => run(() => api.offsetItems(company.id, yearId!, offset))}
        />
      )}

      {view === 'fx' && (
        <FxRevaluationPanel
          company={company}
          t={t}
          locale={locale}
          editable={editable && !closed}
          onGo={onGo}
          // Po zaksięgowaniu wyceny salda są inne — dziennik i zestawienie muszą to pokazać
          onPosted={() => void load()}
        />
      )}

      {view === 'journal' && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.journal.open}</h4>
              <p>{t.journal.entryCount(entries?.length ?? 0)}</p>
            </div>
          </div>

          {entries?.length === 0 && <p className="muted">{t.journal.empty}</p>}

          {entries && entries.length > 0 && (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th className="n">{t.journal.entryNo}</th>
                    <th>{t.journal.postingDate}</th>
                    <th>{t.journal.description}</th>
                    <th>{t.journal.lines}</th>
                    <th className="n">{t.journal.amount}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="n mono">{entry.entryNo}</td>
                      <td>{written(entry.postingDate, locale)}</td>
                      <td>
                        {entry.description}
                        {entry.openingBalance && (
                          <span className="chip st-info" style={{ marginLeft: 6 }}>
                            {t.journal.opening}
                          </span>
                        )}
                        {entry.reversalOfEntryId && (
                          <span className="chip st-draft" style={{ marginLeft: 6 }}>
                            {t.journal.reversal}
                          </span>
                        )}
                      </td>
                      <td className="sm">
                        {entry.lines.map((line) => (
                          <div key={line.lineNo}>
                            <span className="mono">{line.account}</span> {line.accountName}
                            <span className="muted">
                              {' · '}
                              {line.side === 'DEBIT' ? t.journal.debit : t.journal.credit}{' '}
                              {money(line.amount, locale)}
                            </span>
                            {line.counterpartyName && <span className="muted"> · {line.counterpartyName}</span>}
                          </div>
                        ))}
                      </td>
                      <td className="n mono">{money(entry.totalDebit, locale)}</td>
                      <td className="act">
                        {editable && !closed && !entry.reversalOfEntryId && (
                          <button
                            type="button"
                            className="btn ghost sm"
                            onClick={() => setReversing({ id: entry.id, reason: '' })}
                          >
                            {t.journal.reverse}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {reversing && (
            <div className="alert a-warn">
              <Icon.Warn />
              <div>
                <b>{t.journal.reverseTitle}</b>
                <p>{t.journal.reverseHint}</p>
                <input
                  className="inp"
                  value={reversing.reason}
                  placeholder={t.journal.reverseReason}
                  onChange={(event) => setReversing({ ...reversing, reason: event.target.value })}
                />
              </div>
              <button
                type="button"
                className="btn"
                disabled={busy || reversing.reason.trim() === ''}
                onClick={() =>
                  void run(async () => {
                    await api.reverseEntry(company.id, yearId!, reversing.id, reversing.reason);
                    setReversing(null);
                  })
                }
              >
                {t.journal.reverse}
              </button>
            </div>
          )}
        </div>
      )}

      {view === 'balance' && balance && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.journal.trialBalance}</h4>
              <p>{t.journal.trialBalanceHint}</p>
            </div>
            <div className="sp">
              {/* Trzy równości naraz; fałsz znaczy, że księgi nie są gotowe do sprawozdania */}
              <span className={balance.balanced ? 'chip st-ok' : 'chip st-bad'}>
                {balance.balanced ? t.journal.controlOk : t.journal.controlFailed}
              </span>
            </div>
          </div>

          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.journal.account}</th>
                  <th>{t.journal.name}</th>
                  <th className="n">{t.journal.openingDebit}</th>
                  <th className="n">{t.journal.openingCredit}</th>
                  <th className="n">{t.journal.turnoverDebit}</th>
                  <th className="n">{t.journal.turnoverCredit}</th>
                  <th className="n">{t.journal.closingDebit}</th>
                  <th className="n">{t.journal.closingCredit}</th>
                </tr>
              </thead>
              <tbody>
                {balance.rows.map((row) => (
                  <tr key={row.account}>
                    <td className="mono">{row.account}</td>
                    <td>{row.name}</td>
                    <td className="n mono">{money(row.openingDebit, locale)}</td>
                    <td className="n mono">{money(row.openingCredit, locale)}</td>
                    <td className="n mono">{money(row.turnoverDebit, locale)}</td>
                    <td className="n mono">{money(row.turnoverCredit, locale)}</td>
                    <td className="n mono">{money(row.closingDebit, locale)}</td>
                    <td className="n mono">{money(row.closingCredit, locale)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>
                    <b>{t.journal.totals}</b>
                  </td>
                  <td className="n mono">{money(balance.openingDebit, locale)}</td>
                  <td className="n mono">{money(balance.openingCredit, locale)}</td>
                  <td className="n mono">{money(balance.turnoverDebit, locale)}</td>
                  <td className="n mono">{money(balance.turnoverCredit, locale)}</td>
                  <td className="n mono">{money(balance.closingDebit, locale)}</td>
                  <td className="n mono">{money(balance.closingCredit, locale)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="note">{t.journal.offBalanceNote}</p>
        </div>
      )}
    </>
  );
}
